import { Table } from '@tanstack/react-table'
import { ExportHiddenColumnsSchema } from 'utils/table/exportHiddenColumns'

export type ExportHeadersListEntry = {
    key: string
    translation: string
    position: number
}

const TABLE_UI_ONLY_COLUMNS = new Set(['select', 'actions'])

/** Visible table columns → backend export fields, in UI column order. */
export const getVisibleExportFieldsFromTable = (opts: {
    table: Table<any>
    schema: ExportHiddenColumnsSchema
}): string[] => {
    const { table, schema } = opts
    const exportFieldSet = new Set(schema.exportFields)
    const nonExportable = new Set(schema.nonExportableUiColumns ?? [])
    const orderedFields: string[] = []
    const seen = new Set<string>()

    const addField = (field: string) => {
        if (!exportFieldSet.has(field) || seen.has(field)) {
            return
        }

        seen.add(field)
        orderedFields.push(field)
    }

    table.getVisibleLeafColumns().forEach((column) => {
        const columnId = column.id
        if (TABLE_UI_ONLY_COLUMNS.has(columnId) || nonExportable.has(columnId)) {
            return
        }

        const mappedFields = schema.visibleUiColumnsMap[columnId]
        if (mappedFields && mappedFields.length > 0) {
            mappedFields.forEach(addField)
            return
        }

        addField(columnId)
    })

    return orderedFields
}

/** Everything in exportFields that is not currently visible in the table. */
export const getHiddenExportColumnsFromTable = (opts: {
    table: Table<any>
    schema: ExportHiddenColumnsSchema
}): string[] => {
    const visibleFields = new Set(getVisibleExportFieldsFromTable(opts))

    return opts.schema.exportFields.filter((field) => !visibleFields.has(field))
}

export const buildExportHeadersList = (
    orderedFieldNames: string[],
    resolveTranslation: (fieldName: string) => string
): ExportHeadersListEntry[] => {
    return orderedFieldNames.map((fieldName, position) => ({
        key: fieldName,
        translation: resolveTranslation(fieldName),
        position,
    }))
}
